import { CalendarMonth } from "@mui/icons-material";
import { InputAdornment, Switch } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useMemo } from "react";
import { Controller, ControllerProps, FieldError, Path, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbPlane } from "react-icons/tb";

import { CommonDatePickerPlain, CommonHelperText, CommonInput, CommonLabel } from "@/components/CommonInput";
import { getValueByPath } from "@/helpers/object";

import { CreatePassengerForm } from "./CreateCustomPassengerDialog";

function CreatePassengerFlightInfo({
    loading,
    addFlightTimeInfo,
    formBasePath,
}: {
    formBasePath: "step2.arrival.info" | "step2.departure.info";
    addFlightTimeInfo: boolean;
    loading: boolean;
}) {
    const { t, i18n } = useTranslation();
    const { control, formState, resetField, register } = useFormContext<CreatePassengerForm>();

    const flightTimeError = useMemo(
        () => getValueByPath<FieldError>(formState.errors, `${formBasePath}.flightTime`),
        [formBasePath, formState]
    );
    const flightNumberError = useMemo(
        () => getValueByPath<FieldError>(formState.errors, `${formBasePath}.flightNumber`),
        [formBasePath, formState]
    );
    return (
        <div className=" grid grid-cols-8 col-span-full gap-5  py-2">
            <div className="flex col-span-full p-2   items-center justify-between rounded-t-lg border-b bg-gray-100 ">
                <div className="space-y-0.5 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <TbPlane className="h-4 w-4 " />
                    </div>
                    <div>
                        <div className="text-base font-medium">Uçak Bilgileri</div>
                        <p className="text-[13px] text-gray-500">
                            Uçak bilgilerini eklemek için seçeneği etkinleştirebilirsiniz.
                        </p>
                    </div>
                </div>
                <Controller
                    name={(formBasePath + ".addFlightInfo") as Path<CreatePassengerForm>}
                    control={control}
                    render={({ field }) => (
                        <Switch
                            {...field}
                            checked={!!field.value}
                            onChange={(e) => {
                                resetField(`${formBasePath}.flightNumber`);
                                resetField(`${formBasePath}.flightTime`);

                                field.onChange(e.target.checked);
                            }}
                        />
                    )}
                />
            </div>
            {addFlightTimeInfo && (
                <>
                    <CommonLabel required label={t("flight-number")} className=" col-span-4">
                        <CommonInput
                            error={!!flightNumberError?.message}
                            {...register((formBasePath + ".flightNumber") as Path<CreatePassengerForm>)}
                            placeholder={t("placeholders.enter", { field: t("flight-number") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbPlane />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={flightNumberError?.message || ""} />
                    </CommonLabel>
                    <CommonLabel label={t("flight-time")} className="col-span-4">
                        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
                            <Controller
                                name={
                                    (formBasePath + ".flightTime") as ControllerProps<
                                        CreatePassengerForm,
                                        "step2.arrival.info.flightTime"
                                    >["name"]
                                }
                                control={control}
                                render={({ field }) => (
                                    <CommonDatePickerPlain
                                        {...field}
                                        ampm={false}
                                        disabled={loading}
                                        slotProps={{
                                            textField: () => ({
                                                variant: "filled",
                                                error: !!flightTimeError?.message,
                                                helperText: flightTimeError?.message || "",
                                                InputProps: {
                                                    startAdornment: (
                                                        <InputAdornment className=" -translate-y-2" position="start">
                                                            <CalendarMonth fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }),
                                        }}
                                        onChange={(value) => field.onChange(value)}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </CommonLabel>
                </>
            )}
        </div>
    );
}

export default CreatePassengerFlightInfo;
